import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Wine,
  Users,
  Moon,
  Heart,
  Briefcase
} from "lucide-react";

// Compatibility questions
const QUESTIONS = [
  {
    id: "alcohol",
    question: "Do you drink alcohol socially?",
    icon: Wine,
    options: [
      { value: "never", label: "Never - I'm sober", score: 100 },
      { value: "rarely", label: "Rarely - special occasions only", score: 80 },
      { value: "sometimes", label: "Sometimes - social settings", score: 50 },
      { value: "often", label: "Often - it's part of my lifestyle", score: 20 },
    ],
    weight: 1.5, // High weight - sober environment is important
  },
  {
    id: "environment",
    question: "What type of environment do you prefer?",
    icon: Users,
    options: [
      { value: "quiet", label: "Quiet focus - I need space to work", score: 80 },
      { value: "balanced", label: "Balanced - work and social time", score: 100 },
      { value: "social", label: "Social - I love being around people", score: 90 },
      { value: "party", label: "Very social - I'm always up for fun", score: 40 },
    ],
    weight: 1.2,
  },
  {
    id: "sleep",
    question: "What time do you usually go to sleep?",
    icon: Moon,
    options: [
      { value: "early", label: "Before 10pm - early bird", score: 100 },
      { value: "normal", label: "10pm - 12am - pretty normal", score: 90 },
      { value: "late", label: "12am - 2am - night owl", score: 60 },
      { value: "very_late", label: "After 2am - I'm nocturnal", score: 30 },
    ],
    weight: 1.3, // Important for quiet hours
  },
  {
    id: "why_savi",
    question: "Why are you interested in staying at Savi?",
    icon: Heart,
    options: [
      { value: "community", label: "Community & connections", score: 100 },
      { value: "focus", label: "Focused work environment", score: 95 },
      { value: "nature", label: "Nature & peaceful setting", score: 90 },
      { value: "affordable", label: "Affordable accommodation", score: 50 },
      { value: "party", label: "Meet people and have fun", score: 30 },
    ],
    weight: 1.4,
  },
  {
    id: "project",
    question: "What are you currently working on?",
    icon: Briefcase,
    type: "text",
    placeholder: "Tell us about your work, project, or what you're exploring...",
    weight: 0, // No score, just info
  },
];

const CompatibilityPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [textAnswer, setTextAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };
  const question = QUESTIONS[currentQuestion];
  const progress = ((currentQuestion + 1) / QUESTIONS.length) * 100;

  const handleOptionSelect = (value) => {
    setAnswers({ ...answers, [question.id]: value });
    
    // Auto-advance to next question
    setTimeout(() => {
      if (currentQuestion < QUESTIONS.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        submitAnswers({ ...answers, [question.id]: value });
      }
    }, 300);
  };

  const handleTextSubmit = () => {
    if (!textAnswer.trim()) {
      toast.error("Please tell us a bit about yourself");
      return;
    }
    
    const finalAnswers = { ...answers, [question.id]: textAnswer };
    submitAnswers(finalAnswers);
  };

  const submitAnswers = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/compatibility/submit`,
        { answers: finalAnswers },
        { headers }
      );
      setResult(res.data);
    } catch (error) {
      toast.error("Failed to submit questionnaire");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const goBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else {
      navigate(-1);
    }
  };

  // Show result screen
  if (result) {
    const isGoodFit = result.score >= 70;
    
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex flex-col">
        <header className="px-4 py-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/onboarding")}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Continue Onboarding
          </Button>
        </header>

        <div className="flex-1 flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center">
            <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-6 ${
              isGoodFit ? "bg-[#81B29A]" : "bg-[#E07A5F]"
            }`}>
              <span className="text-4xl text-white font-bold">{result.score}%</span>
            </div>
            
            <h1 className="font-serif text-3xl text-[#3D4035] mb-2">
              {isGoodFit ? "Great Match! 🎉" : "Hmm, Let's Chat 💬"}
            </h1>
            
            <p className="text-[#3D4035]/70 mb-6">
              {result.message}
            </p>

            {result.notes && (
              <div className="bg-[#F4F1DE] rounded-2xl p-4 mb-6 text-left">
                <p className="text-sm text-[#3D4035]/80">{result.notes}</p>
              </div>
            )}

            <Button 
              onClick={() => navigate("/onboarding")}
              className="w-full bg-[#E07A5F] hover:bg-[#D06A4F]"
            >
              Continue to Onboarding
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCF8] flex flex-col">
      {/* Header */}
      <header className="px-4 py-4 flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={goBack}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <span className="text-sm text-[#3D4035]/60">
          {currentQuestion + 1} of {QUESTIONS.length}
        </span>
      </header>

      {/* Progress */}
      <div className="px-4">
        <Progress value={progress} className="h-1" />
      </div>

      {/* Question */}
      <div className="flex-1 flex flex-col px-4 py-8">
        <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto bg-[#F4F1DE] rounded-2xl flex items-center justify-center mb-4">
              <question.icon className="w-8 h-8 text-[#E07A5F]" />
            </div>
            <h2 className="font-serif text-2xl text-[#3D4035]">
              {question.question}
            </h2>
          </div>

          {question.type === "text" ? (
            <div className="space-y-4">
              <Textarea
                value={textAnswer}
                onChange={(e) => setTextAnswer(e.target.value)}
                placeholder={question.placeholder}
                className="min-h-[150px] rounded-2xl"
              />
              <Button
                onClick={handleTextSubmit}
                disabled={submitting}
                className="w-full bg-[#E07A5F] hover:bg-[#D06A4F]"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>Complete <Check className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {question.options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleOptionSelect(option.value)}
                  className={`w-full p-4 rounded-2xl text-left transition-all ${
                    answers[question.id] === option.value
                      ? "bg-[#E07A5F] text-white"
                      : "bg-white border border-stone-200 hover:border-[#E07A5F] text-[#3D4035]"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompatibilityPage;
